import {
    IonBadge,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonProgressBar,
    IonRefresher,
    IonRefresherContent,
    IonSegment,
    IonSegmentButton,
    IonTitle,
    IonToolbar,
    RefresherEventDetail
} from "@ionic/react";
import {
    arrowBackOutline,
    chevronForwardOutline,
    americanFootballOutline,
    refreshOutline,
    trophyOutline
} from "ionicons/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
    CompetitionDetail,
    Team,
    TeamDetail,
    getCompetitionDetail,
    getTeamDetail,
    getTeams
} from "../services/mexfbaApi";
import {
    StandingRow,
    createStandingsByGroup
} from "../utils/competitionStandings";
import "./TeamTable.css";

interface RouteParams {
    id: string;
}

function getTeamInitial(name?: string): string {
    return name?.trim().charAt(0).toUpperCase() || "?";
}

const TeamTable: React.FC = () => {
    const { id } = useParams<RouteParams>();
    const history = useHistory();

    const [teamDetail, setTeamDetail] =
        useState<TeamDetail | null>(null);

    const [competitionDetail, setCompetitionDetail] =
        useState<CompetitionDetail | null>(null);

    const [teams, setTeams] = useState<Team[]>([]);
    const [selectedGroup, setSelectedGroup] = useState("");
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");

    const loadTable = useCallback(
        async function (forceRefresh = false) {
            try {
                setError("");

                if (forceRefresh) {
                    setRefreshing(true);
                } else {
                    setLoading(true);
                }

                const [teamData, teamsData] = await Promise.all([
                    getTeamDetail(id, forceRefresh),
                    getTeams(forceRefresh)
                ]);

                if (!teamData.Info.comp_id) {
                    throw new Error(
                        "El equipo no tiene una competencia activa asignada."
                    );
                }

                const competitionData = await getCompetitionDetail(
                    teamData.Info.comp_id,
                    forceRefresh
                );

                setTeamDetail(teamData);
                setCompetitionDetail(competitionData);
                setTeams(teamsData);

                const currentTeam = competitionData.Equipos[id];

                setSelectedGroup(function (currentGroup) {
                    if (currentGroup) {
                        return currentGroup;
                    }

                    return currentTeam?.grupo || "";
                });
            } catch (requestError) {
                console.error(
                    "No fue posible cargar la tabla del equipo:",
                    requestError
                );

                setError(
                    requestError instanceof Error
                        ? requestError.message
                        : "No fue posible cargar la tabla de posiciones."
                );
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        [id]
    );

    useEffect(
        function () {
            loadTable();
        },
        [loadTable]
    );

    const standingsByGroup = useMemo(function () {
        if (!competitionDetail) {
            return new Map<string, StandingRow[]>();
        }

        return createStandingsByGroup(
            Object.values(competitionDetail.Equipos || {}),
            Object.values(competitionDetail.Partidos || {})
        );
    }, [competitionDetail]);

    const groups = useMemo(function () {
        return Array.from(standingsByGroup.keys()).sort(function (
            firstGroup,
            secondGroup
        ) {
            return firstGroup.localeCompare(secondGroup, "es-MX", {
                numeric: true
            });
        });
    }, [standingsByGroup]);

    const activeGroup = useMemo(function () {
        if (selectedGroup && standingsByGroup.has(selectedGroup)) {
            return selectedGroup;
        }

        return groups[0] || "";
    }, [groups, selectedGroup, standingsByGroup]);

    const selectedStandings = useMemo(function () {
        return standingsByGroup.get(activeGroup) || [];
    }, [activeGroup, standingsByGroup]);

    const logosByTeamId = useMemo(function () {
        return new Map(
            teams.map(function (team) {
                return [team.id, team.logo || ""];
            })
        );
    }, [teams]);

    async function handleRefresh(
        event: CustomEvent<RefresherEventDetail>
    ) {
        await loadTable(true);
        event.detail.complete();
    }

    if (loading) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonButton
                                aria-label="Regresar"
                                onClick={function () {
                                    history.goBack();
                                }}
                            >
                                <IonIcon icon={arrowBackOutline} />
                            </IonButton>
                        </IonButtons>

                        <IonTitle>Tabla</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent className="team-table-content">
                    <IonProgressBar type="indeterminate" />
                </IonContent>
            </IonPage>
        );
    }

    if (error || !teamDetail || !competitionDetail) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonButton
                                aria-label="Regresar"
                                onClick={function () {
                                    history.goBack();
                                }}
                            >
                                <IonIcon icon={arrowBackOutline} />
                            </IonButton>
                        </IonButtons>

                        <IonTitle>Tabla</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent className="team-table-content">
                    <div className="team-table-empty">
                        <IonIcon icon={americanFootballOutline} />

                        <strong>No fue posible cargar la tabla</strong>

                        <span>{error}</span>

                        <IonButton
                            onClick={function () {
                                loadTable(true);
                            }}
                        >
                            Reintentar
                        </IonButton>
                    </div>
                </IonContent>
            </IonPage>
        );
    }

    const info = teamDetail.Info;

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar className="team-table-toolbar">
                    <IonButtons slot="start">
                        <IonButton
                            aria-label="Regresar al equipo"
                            onClick={function () {
                                history.push(`/team/${id}`);
                            }}
                        >
                            <IonIcon icon={arrowBackOutline} />
                        </IonButton>
                    </IonButtons>

                    <IonTitle>Tabla</IonTitle>

                    <IonButtons slot="end">
                        <IonButton
                            aria-label="Actualizar tabla"
                            disabled={refreshing}
                            onClick={function () {
                                loadTable(true);
                            }}
                        >
                            <IonIcon icon={refreshOutline} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="team-table-content">
                <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
                    <IonRefresherContent
                        pullingText="Desliza para actualizar"
                        refreshingText="Actualizando tabla..."
                    />
                </IonRefresher>

                <section
                    className="team-table-hero"
                    style={
                        info.color
                            ? {
                                  background: `linear-gradient(135deg, ${info.color}, #173e70)`
                              }
                            : undefined
                    }
                >
                    {info.logo ? (
                        <img
                            src={info.logo}
                            alt={`Logo de ${info.nombre}`}
                        />
                    ) : (
                        <div className="team-table-hero__placeholder">
                            {getTeamInitial(info.nombre)}
                        </div>
                    )}

                    <div>
                        <span>TABLA DE POSICIONES</span>
                        <h1>{info.nombre}</h1>
                        <p>
                            {competitionDetail.Info.nombre ||
                                info.comp_nombre ||
                                "Competencia actual"}
                        </p>
                    </div>
                </section>

                <section className="team-table-navigation">
                    <IonButton
                        fill="outline"
                        onClick={function () {
                            history.push(`/team/${id}`);
                        }}
                    >
                        Calendario
                    </IonButton>

                    <IonButton>Tabla</IonButton>

                    <IonButton
                        fill="outline"
                        onClick={function () {
                            history.push(`/statsTeam/${id}`);
                        }}
                    >
                        Jugadores
                    </IonButton>

                    <IonButton
                        fill="outline"
                        onClick={function () {
                            history.push(`/teamStatsTeam/${id}`);
                        }}
                    >
                        Equipo
                    </IonButton>

                    <IonButton
                        fill="outline"
                        onClick={function () {
                            history.push(`/rosterTeam/${id}`);
                        }}
                    >
                        Plantel
                    </IonButton>
                </section>

                <section className="team-table-heading">
                    <div>
                        <span>CLASIFICACIÓN</span>
                        <h2>
                            {groups.length > 1
                                ? "Tabla de grupos"
                                : "Posiciones generales"}
                        </h2>
                    </div>

                    <IonIcon icon={trophyOutline} />
                </section>

                {groups.length > 1 && (
                    <IonSegment
                        className="team-table-groups"
                        value={activeGroup}
                        scrollable
                        onIonChange={function (event) {
                            setSelectedGroup(
                                String(event.detail.value || "")
                            );
                        }}
                    >
                        {groups.map(function (group) {
                            return (
                                <IonSegmentButton
                                    key={group}
                                    value={group}
                                >
                                    Grupo {group}
                                </IonSegmentButton>
                            );
                        })}
                    </IonSegment>
                )}

                <section className="team-table-card">
                    <div className="team-table-card__group">
                        <span>
                            {activeGroup
                                ? `Grupo ${activeGroup}`
                                : "Clasificación"}
                        </span>

                        <IonBadge>
                            {selectedStandings.length} equipos
                        </IonBadge>
                    </div>

                    <div className="team-table-scroll">
                        <table>
                            <thead>
                                <tr>
                                    <th>POS</th>
                                    <th>EQUIPO</th>
                                    <th>PJ</th>
                                    <th>G</th>
                                    <th>E</th>
                                    <th>P</th>
                                    <th>PF</th>
                                    <th>PC</th>
                                    <th>DIF</th>
                                </tr>
                            </thead>

                            <tbody>
                                {selectedStandings.map(function (row) {
                                    const isCurrentTeam = row.id === id;
                                    const logo = logosByTeamId.get(row.id);

                                    return (
                                        <tr
                                            className={
                                                isCurrentTeam
                                                    ? "team-table-row--current"
                                                    : ""
                                            }
                                            key={row.id}
                                        >
                                            <td>
                                                <span className="team-table-position">
                                                    {row.position}
                                                </span>
                                            </td>

                                            <td>
                                                <button
                                                    className="team-table-team"
                                                    type="button"
                                                    onClick={function () {
                                                        history.push(
                                                            `/team/${row.id}`
                                                        );
                                                    }}
                                                >
                                                    {logo ? (
                                                        <img
                                                            src={logo}
                                                            alt=""
                                                        />
                                                    ) : (
                                                        <span>
                                                            {getTeamInitial(
                                                                row.nombre
                                                            )}
                                                        </span>
                                                    )}

                                                    <strong>
                                                        {row.nombre}
                                                    </strong>
                                                </button>
                                            </td>

                                            <td>{row.played}</td>
                                            <td>{row.wins}</td>
                                            <td>{row.draws}</td>
                                            <td>{row.losses}</td>
                                            <td>{row.pointsFor}</td>
                                            <td>{row.pointsAgainst}</td>
                                            <td
                                                className={
                                                    row.difference > 0
                                                        ? "team-table-difference--positive"
                                                        : row.difference < 0
                                                        ? "team-table-difference--negative"
                                                        : ""
                                                }
                                            >
                                                {row.difference > 0
                                                    ? `+${row.difference}`
                                                    : row.difference}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </section>

                <section className="team-table-note">
                    <IonIcon icon={chevronForwardOutline} />
                    <span>
                        Tu equipo se resalta en azul dentro de la tabla.
                    </span>
                </section>
            </IonContent>
        </IonPage>
    );
};

export default TeamTable;
